import { useSyncExternalStore } from 'react';

/**
 * Below this width the side nav is an off-canvas drawer (app.css): hidden
 * until the top bar's menu button opens it over the page, so neither an
 * expanded 240 px nav nor the 56 px rail takes width from a phone or a
 * foldable / tablet held upright (mobile audit 2026-10-21: an 860 px
 * foldable kept the 240 px nav and the schedule got 620 px). Keep in step
 * with the matching media queries in app.css.
 */
export const NARROW_SHELL_QUERY = '(width < 900px)';

/** Phone width: pages pick their phone layouts (the schedule's agenda) here. */
export const PHONE_QUERY = '(width <= 720px)';

/** Compact page chrome (one-line toolbars, menus instead of button rows). Same as the drawer. */
export const COMPACT_QUERY = NARROW_SHELL_QUERY;

/** Live `matchMedia(query).matches` (false where matchMedia is missing, e.g. tests). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
      const mq = window.matchMedia(query);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    () =>
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false,
    () => false,
  );
}

/** True below 900 px: the schedule and other dense pages use their compact chrome. */
export function useCompact(): boolean {
  return useMediaQuery(COMPACT_QUERY);
}

/** True at phone width (720 px and under). */
export function usePhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(NARROW_SHELL_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function snapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(NARROW_SHELL_QUERY).matches;
}

/** True while the window is phone width. */
export function useNarrowShell(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}

/**
 * The nav state actually shown. On a narrow window the saved preference is
 * ignored and the rail is used, unless the person opened the nav for this
 * visit (`narrowOpen`), which is never saved.
 */
export function effectiveNavCollapsed(
  saved: boolean,
  narrow: boolean,
  narrowOpen: boolean,
): boolean {
  return narrow ? !narrowOpen : saved;
}

/**
 * Tablet width (TC role test fix 23): pages with a three-column workspace
 * (the treatment planner) start with the nav as the icon rail below this
 * width, so the plan, its lines and the rail fit at 1024 px. The saved
 * preference still applies on other pages and on wider screens.
 */
export const TIGHT_SHELL_QUERY = '(width <= 1200px)';

const WIDE_WORKSPACES = [/^\/treatment-plans(\/|$)/, /^\/patients\/[^/]+\/treatment(\/|$)/];

/** Whether this page wants the rail at tablet width (pure; tested). */
export function wantsRailWhenTight(pathname: string): boolean {
  return WIDE_WORKSPACES.some((re) => re.test(pathname));
}

function subscribeTight(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(TIGHT_SHELL_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function snapshotTight(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(TIGHT_SHELL_QUERY).matches;
}

/** True while the window is tablet width or narrower. */
export function useTightShell(): boolean {
  return useSyncExternalStore(subscribeTight, snapshotTight, () => false);
}
